import * as R from 'ramda';

import { DropResult } from '@atom/components/common/dragAndDrop';
import {
  ArrowDropDownCircleIcon,
  AttachMoneyIcon,
  DoneAllIcon,
  FormatAlignLeftIcon,
  IconComponent,
  LooksOneIcon,
  RadioButtonCheckedIcon,
  ShortTextIcon,
  TodayIcon,
} from '@atom/mui/icons';
import { AttributeDataType, DataType } from '@atom/types/dataType';
import { SchemaTreeAttribute } from '@atom/types/schema';

import { PendingUpdates } from '../../SchemaDetailContext';

export interface DataTypeOption {
  dataType: AttributeDataType;
  title: string;
  iconComponent: IconComponent;
}

// dataTypes that require debounced onChange
export const DEBOUNCED_DATA_TYPES = new Set([
  DataType.SHORT_TEXT,
  DataType.LONG_TEXT,
  DataType.NUMBER,
  DataType.CURRENCY,
]);

export const DATA_TYPE_OPTIONS: DataTypeOption[] = [
  {
    dataType: DataType.SHORT_TEXT,
    title: 'Short Text',
    iconComponent: ShortTextIcon,
  },
  {
    dataType: DataType.LONG_TEXT,
    title: 'Long Text',
    iconComponent: FormatAlignLeftIcon,
  },
  {
    dataType: DataType.ENUM_SINGLE,
    title: 'Single Select',
    iconComponent: ArrowDropDownCircleIcon,
  },
  {
    dataType: DataType.ENUM_MULTIPLE,
    title: 'Multi Select',
    iconComponent: DoneAllIcon,
  },
  {
    dataType: DataType.BOOLEAN,
    title: 'Yes / No',
    iconComponent: RadioButtonCheckedIcon,
  },
  {
    dataType: DataType.NUMBER,
    title: 'Numeric',
    iconComponent: LooksOneIcon,
  },
  {
    dataType: DataType.CURRENCY,
    title: 'Currency',
    iconComponent: AttachMoneyIcon,
  },
  {
    dataType: DataType.DATE,
    title: 'Date Picker',
    iconComponent: TodayIcon,
  },
];

export const FILTERABLE_DATA_TYPES = new Set([
  DataType.ENUM_SINGLE,
  DataType.ENUM_MULTIPLE,
  DataType.BOOLEAN,
  DataType.NUMBER,
  DataType.CURRENCY,
  DataType.DATE,
]);

export const getDataTypeChangePayload = (dataType: AttributeDataType) => {
  const defaultPayload = {
    dataType,
    unit: null,
    enumeration: null,
    defaultValue: null,
  };

  const payloads = {
    [DataType.NUMBER]: {
      dataType,
      unit: '',
      enumeration: null,
    },
    [DataType.ENUM_SINGLE]: {
      dataType,
      unit: null,
      enumeration: ['', 'Option 1'],
    },
    [DataType.ENUM_MULTIPLE]: {
      dataType,
      unit: null,
      enumeration: ['Option 1', 'Option 2'],
    },
  };

  return payloads[dataType] || defaultPayload;
};

export const addEnumOption = (
  enumeration: string[],
  isEnumSingle: boolean,
): string[] => {
  // Offset due to enum single empty value in index 0
  const optionNumber = isEnumSingle
    ? enumeration.length
    : enumeration.length + 1;

  return [...enumeration, `Option ${optionNumber}`];
};

export const removeEnumOption = (
  enumeration: string[],
  index: number,
): string[] => {
  return R.remove(index, 1, enumeration);
};

export const updateEnumOption = (
  enumeration: string[],
  index: number,
  value: string,
): string[] => {
  return R.update(index, value, enumeration);
};

export const reorderEnumOptions = (
  enumeration: string[],
  result: DropResult,
): string[] => {
  const sourceIndex = result.source.index;
  const destinationIndex = result.destination.index;

  return R.move(sourceIndex, destinationIndex, enumeration);
};

export const updateTempEnumsIndexes = (
  currentSet: Set<number>,
  result: DropResult,
): Set<number> => {
  const updatedTempEnumsIndexes: Set<number> = new Set();

  currentSet.forEach(currentTempEnumIndex => {
    // If the tempEnumIndex is the one being moved, update it's location
    if (currentTempEnumIndex === result.source.index) {
      updatedTempEnumsIndexes.add(result.destination.index);
      return;
    }
    // If the moved item is raised past the stored tempEnumIndex, add 1
    if (
      currentTempEnumIndex < result.source.index &&
      currentTempEnumIndex >= result.destination.index
    ) {
      updatedTempEnumsIndexes.add(currentTempEnumIndex + 1);
      return;
    }
    // If the moved item is lowered below the stored tempEnumIndex, subtract 1
    if (
      currentTempEnumIndex > result.source.index &&
      currentTempEnumIndex <= result.destination.index
    ) {
      updatedTempEnumsIndexes.add(currentTempEnumIndex - 1);
      return;
    }
    // If the stored tempEnumIndex is not effected by the move, remain the same
    updatedTempEnumsIndexes.add(currentTempEnumIndex);
  });

  return updatedTempEnumsIndexes;
};

export const removeTempEnumIndex = (
  currentSet: Set<number>,
  removedIndex: number,
): Set<number> => {
  const updatedTempEnumsIndexes: Set<number> = new Set();

  currentSet.forEach(currentTempEnumIndex => {
    // If the tempEnumIndex is being removed, take it out of the Set
    if (currentTempEnumIndex === removedIndex) {
      return;
    }
    // If the removed option comes before, reduce the tempEnumIndex by 1
    if (currentTempEnumIndex > removedIndex) {
      updatedTempEnumsIndexes.add(currentTempEnumIndex - 1);
      return;
    }
    // If the tempEnumIndex is not effected by the removal, stay the same
    updatedTempEnumsIndexes.add(currentTempEnumIndex);
  });

  return updatedTempEnumsIndexes;
};

// Handles updating pendingUpdates state tree
export const updatePendingUpdates = (
  schemaId: string,
  pendingUpdates: PendingUpdates,
  attribute: Partial<SchemaTreeAttribute>,
  property: keyof SchemaTreeAttribute,
  value: any,
): PendingUpdates => {
  const hasSchemaEntry = R.has(schemaId)(pendingUpdates);
  const hasAttributeEntry = hasSchemaEntry
    ? R.has(attribute.id)(pendingUpdates[schemaId])
    : false;

  const updatedPendingUpdates = {
    ...pendingUpdates,
    [schemaId]: {
      ...(hasSchemaEntry ? pendingUpdates[schemaId] : {}),
      [attribute.id]: {
        id: attribute.id,
        ...(hasAttributeEntry ? pendingUpdates[schemaId][attribute.id] : {}),
        [property]: value,
      },
    },
  };

  return updatedPendingUpdates;
};

// Merges pending changes with the selected attribute
// If an attribute property has pending changes, they will be displayed
// instead of the currently published changes.
export const mergePendingChanges = (
  schemaId: string,
  attribute: Partial<SchemaTreeAttribute>,
  pendingUpdates: PendingUpdates,
): Partial<SchemaTreeAttribute> => {
  const hasSchemaEntry = R.has(schemaId)(pendingUpdates);
  const hasAttributeEntry = hasSchemaEntry
    ? R.has(attribute?.id)(pendingUpdates[schemaId])
    : false;

  const pendingUpdateData =
    hasSchemaEntry && hasAttributeEntry
      ? pendingUpdates[schemaId][attribute?.id]
      : {};

  return { ...attribute, ...pendingUpdateData };
};
